<script lang="ts">
export const elVerticalTabColors = ['primary', 'secondary'] as const;
export type ElVerticalTabColors = (typeof elVerticalTabColors)[number];

export const elVerticalTabStatus = ['default', 'hover', 'active'] as const;
export type ElVerticalTabStatus = (typeof elVerticalTabStatus)[number];
</script>

<script setup lang="ts">
import ElAvatar from '@/ElAvatar.vue';
import ElIcon from '@/ElIcon.vue';
import ElBadge from '@/ElBadge.vue';

defineProps<{
  icon?: InstanceType<typeof ElIcon>['$props']; // prop drilling
  avatar?: InstanceType<typeof ElAvatar>['$props'];
  primaryRow: string;
  secondaryRow?: string;
  tertiaryRow?: string;
  lineClamp?: 1 | 2;
  time?: string;
  badge?: InstanceType<typeof ElBadge>['$props'];
  color?: ElVerticalTabColors;
  status?: ElVerticalTabStatus;
}>();
</script>

<template>
  <button
    class="p-3 w-full max-h-28 flex justify-start items-center gap-3 bg-disabled rounded-lg hover:bg-neutral-hover active:bg-neutral-pressed"
    :class="{
      [`border border-primary-active bg-primary-light-active`]: color === 'primary' && status === 'active',
      [`border border-secondary-active bg-secondary-light-active`]: color === 'secondary' && status === 'active',
      [`border border-white bg-white`]: status === 'default',
      [`border border-transparent bg-neutral-hover`]: status === 'hover',
    }"
  >
    <ElIcon
      v-if="icon"
      v-bind="icon"
      class="w-6 h-6 flex-shrink-0"
      :class="{
        [`text-primary-active`]: color === 'primary' && status === 'active',
        [`text-secondary-active`]: color === 'secondary' && status === 'active',
        'text-neutral-darker': !status || status === 'default',
      }"
    />
    <ElAvatar v-if="avatar" v-bind="avatar" />
    <div
      class="flex-1 flex flex-col items-start text-left"
      :class="{
        [`text-${color}-active`]: status === 'active',
        'gap-1': secondaryRow || tertiaryRow,
        'text-neutral-darker': !status || status === 'default',
      }"
    >
      <p class="text-sm font-semibold line-clamp-1">
        {{ primaryRow }}
      </p>
      <p
        class="text-sm font-normal"
        :class="{
          [`line-clamp-1`]: lineClamp === 1,
          [`line-clamp-2`]: lineClamp === 2,
        }"
      >
        {{ secondaryRow }}
      </p>
      <span v-if="tertiaryRow" class="text-neutral-lighter text-sm font-normal line-clamp-1">{{ tertiaryRow }}</span>
    </div>
    <div class="flex flex-col space-y-1 justify-center items-center self-stretch">
      <time
        v-if="time"
        :datetime="time"
        class="text-primary text-xs font-normal leading-5"
        :class="{
          [`text-primary-active`]: color === 'primary' && status === 'active',
          [`text-secondary-active`]: color === 'secondary' && status === 'active',
          'text-neutral-darker': !status || status === 'default',
        }"
        >{{ time }}</time
      >
      <div class="flex justify-center items-center flex-grow">
        <span
          :class="{
            [`text-primary-active`]: color === 'primary' && status === 'active',
            [`text-secondary-active`]: color === 'secondary' && status === 'active',
            'text-neutral-darker': !status || status === 'default',
          }"
          @click.stop
        >
          <slot name="traling-slot">
            <ElIcon class="w-5 h-5 flex-shrink-0" name="ChevronRightIcon" />
          </slot>
        </span>
      </div>
      <div v-if="badge" class="h-4 relative flex justify-center items-end">
        <ElBadge v-bind="badge" />
      </div>
    </div>
  </button>
</template>
